import { store } from "Redux/createStore";
import { userLogout } from "Redux/Actions";
import { redirectToKeycloakLogin } from "../keycloak";
import { getFreshToken } from "./baseURL";

let hasTriggeredAuthLogout = false;
let hasTriggeredNotFoundRedirect = false;

const hasAuthorizationHeader = (headers) => {
  if (!headers) return false;
  return Boolean(headers.Authorization || headers.authorization);
};

const shouldAttachAuth = (config) =>
  config?.skipAuthRefresh !== true &&
  config?.skipAuthHeader !== true &&
  config?.skipAuthRedirect !== true;

const NOT_FOUND_REDIRECT_TARGETS = {
  gameClient: "/games",
  fallbackGameClient: "/games",
  challengeClient: "/challenge-hub",
};

const normalizePathname = (pathname) => {
  const normalized = String(pathname || "/").replace(/\/+$/, "");
  return normalized || "/";
};

export const isAlreadyAtRedirectTarget = (target, location = window.location) => {
  if (!target || !location) return false;

  try {
    const targetUrl = new URL(target, location.origin);
    return (
      targetUrl.origin === location.origin &&
      normalizePathname(targetUrl.pathname) ===
        normalizePathname(location.pathname)
    );
  } catch {
    return false;
  }
};

const normalizeMessage = (value) => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value.map(normalizeMessage).filter(Boolean).join(" ");
  }

  return "";
};

const isRawHttpErrorMessage = (message) =>
  /^(network error|request failed)$/i.test(message) ||
  /request failed with status code \d{3}/i.test(message) ||
  /^(bad request|unauthorized|forbidden|not found|internal server error|service unavailable|gateway timeout)$/i.test(
    message,
  ) ||
  /\bstatus\s+\d{3}\b/i.test(message);

export const PENDING_TOAST_KEY = "kewordal:pendingToast";
const getBackendErrorMessage = (data) => {
  if (typeof data === "string") {
    return normalizeMessage(data);
  }

  if (data && typeof data === "object") {
    return normalizeMessage(
      data.message || data.error || data.detail || data.title,
    );
  }

  return "";
};

export const UNAUTHORIZED_MESSAGE =
  "Your session has expired. Please sign in again.";
export const FORBIDDEN_MESSAGE =
  "User is not permitted to access this resource.";
export const SERVER_ERROR_MESSAGE =
  "Something went wrong. Please try again later.";
export const DEFAULT_ERROR_MESSAGE = "Something went wrong";
export const NETWORK_ERROR_MESSAGE =
  "Unable to connect right now. Please check your connection and try again.";

export const getFrontendErrorMessage = (error) => {
  const status = error?.response?.status;
  const backendMessage = getBackendErrorMessage(error?.response?.data);

  if (status === 401) {
    return UNAUTHORIZED_MESSAGE;
  }

  if (status === 403) {
    return backendMessage || FORBIDDEN_MESSAGE;
  }

  if (status >= 500) {
    return SERVER_ERROR_MESSAGE;
  }

  if (status >= 400 && status < 500) {
    return backendMessage || DEFAULT_ERROR_MESSAGE;
  }

  const rawMessage = normalizeMessage(error?.message);
  if (rawMessage && !isRawHttpErrorMessage(rawMessage)) {
    return rawMessage;
  }

  if (/network error/i.test(rawMessage)) {
    return NETWORK_ERROR_MESSAGE;
  }

  return DEFAULT_ERROR_MESSAGE;
};

export const decorateApiError = (error) => {
  const message = getFrontendErrorMessage(error);

  if (error && typeof error === "object") {
    error.message = message;

    if (error.response) {
      error.response.data =
        error.response.data && typeof error.response.data === "object"
          ? { ...error.response.data, message }
          : { message };
    }
  }

  return error;
};

const queuePendingToast = (message, variant = "error") => {
  try {
    sessionStorage.setItem(
      PENDING_TOAST_KEY,
      JSON.stringify({ message, variant }),
    );
  } catch {}
};

export const attachApiInterceptors = (instance, label = "axios") => {
  if (!instance?.interceptors) {
    return () => {};
  }

  const requestInterceptorId = instance.interceptors.request?.use
    ? instance.interceptors.request.use(
        async (config) => {
          if (!shouldAttachAuth(config)) {
            return config;
          }

          const token = await getFreshToken();
          if (token) {
            config.headers = {
              ...(config.headers || {}),
              Authorization: token,
            };
          }
          return config;
        },
        (error) => Promise.reject(error),
      )
    : null;

  if (!instance.interceptors.response?.use) {
    return () => {
      if (requestInterceptorId !== null) {
        instance.interceptors.request?.eject?.(requestInterceptorId);
      }
    };
  }

  const responseInterceptorId = instance.interceptors.response.use(
    (response) => {
      return response;
    },

    (error) => {
      const status = error?.response?.status;
      const config = error?.config || {};
      const url = config.baseURL
        ? `${config.baseURL}${config.url || ""}`
        : config.url;

      const isAuthError = status === 401 || status === 403;

      const wasAuthenticatedRequest = hasAuthorizationHeader(config.headers);
      const isCurrentlyAuthenticated = Boolean(
        store.getState()?.auth?.isAuthenticated,
      );

      if (
        isAuthError &&
        config.skipAuthRedirect !== true &&
        ((status === 401 && wasAuthenticatedRequest) ||
          (!wasAuthenticatedRequest && !isCurrentlyAuthenticated)) &&
        !hasTriggeredAuthLogout
      ) {
        hasTriggeredAuthLogout = true;
        store.dispatch(userLogout());
        console.warn(
          `[api-interceptor:${label}] auth error (${status}) -- sending to login (same flow as the header Login button), returning to ${url ? "the current page" : "home"}`,
          { url },
        );
        redirectToKeycloakLogin(window.location.href);
      }

      const notFoundRedirectTarget = NOT_FOUND_REDIRECT_TARGETS[label];
      if (
        status === 404 &&
        notFoundRedirectTarget &&
        !isAlreadyAtRedirectTarget(notFoundRedirectTarget) &&
        !hasTriggeredNotFoundRedirect
      ) {
        hasTriggeredNotFoundRedirect = true;

        if (isCurrentlyAuthenticated) {
          queuePendingToast(
            "That game or challenge couldn't be found. Here's the games hub instead.",
          );
          window.location.href = notFoundRedirectTarget;
        } else {
          const redirectUri = `${window.location.origin}${notFoundRedirectTarget}`;
          queuePendingToast(
            "That link isn't available. Please log in to continue.",
          );
          redirectToKeycloakLogin(redirectUri);
        }
      }

      return Promise.reject(decorateApiError(error));
    },
  );

  return () => {
    if (requestInterceptorId !== null) {
      instance.interceptors.request?.eject?.(requestInterceptorId);
    }
    instance.interceptors.response.eject(responseInterceptorId);
  };
};

export default attachApiInterceptors;
